import { XMarkIcon } from "@heroicons/react/20/solid";
import { PencilIcon } from "@heroicons/react/24/solid";
import { DebouncedFunc, debounce } from "lodash";
import { DateTime } from "luxon";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import { Alert, AlertType } from "@/atoms-react/alert/index.react.tsx";
import { Button } from "@/atoms-react/button/Button.react.tsx";
import { Dropdown } from "@/atoms-react/dropdown/index.react";
import Flag from "@/atoms-react/flag/Flag.react";
import InputLabel from "@/atoms-react/input/Input.react";
import { Checkbox } from "@/atoms-react/input-checkbox/index.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base, Label } from "@/atoms-react/text/Text.react";
import { useKYCContext } from "@/features/auth/hooks/use-kyc-context";
import { KYCStatusType } from "@/features/kyc/types";
import { useCityPostalCode } from "@/features/user/address/hooks/use-city-code";
import { convert_naturalPerson_DTO } from "@/features/user/converters";
import { useUser } from "@/features/user/hooks/use-user";
import { useNifValidation } from "@/features/user/nif/hooks/use-nif-validation";
import { useTaxAddress } from "@/features/user/tax_address/hooks/use-taxAdress";
import type {
  AddressCreate,
  CityInfoType,
  FullCountry,
  NaturalPersonDTO
} from "@/features/user/types";
import { getFullName } from "@/features/utils/functions.ts";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { Themes } from "@/types/theme.ts";
import { isIdentityValid } from "@/utils/declarative.utils";
import {
  FormTaxAdresseIdentity,
  TaxAddressSchema
} from "../../identity-np-tax-address/form-tax-address-identity";
import { ModalUpdateInfoHelper } from "../../modal-update-info-helper/index.react";
import { formStyle } from "../form-style";
import { handleTaxAddressesCreate } from "./tax-addresses.utils";

const MAX_NATIONALITY_NUMBER = 4;

const cspWithoutActivity = { CHÔMEUR: 14, INACTIF: 18, MILITAIRE: 13, ETUDIANT: 15 };

const isBirthDateValid = (date: string) => {
  if (!date) {
    return false;
  }
  const min = DateTime.now().minus({ years: 18 });
  if (min < DateTime.fromFormat(date, "yyyy-MM-dd")) {
    return false;
  }
  return true;
};

export const DeclarativeForm = () => {
  const { t } = useTranslation(["pass"]);
  const { locale } = useLocale();
  const { useUserProfile, useCountries, useCSP, useActivities, useEditProfile } = useUser();
  const { mutateAsync: editProfile, isPending: isEditing } = useEditProfile();
  const { data: user, refetch: refetchUser, isRefetching: isLoadingUser } = useUserProfile();
  const { data: countriesData } = useCountries(locale.language);
  const { data: CSPCodes } = useCSP(locale.language);
  const { data: activities } = useActivities(locale.language);
  const { status, nextStatus } = useKYCContext();
  const [userForm, setUserForm] = useState<
    NaturalPersonDTO & { isDirty: boolean; isDirtyTA?: boolean }
  >();
  const [modalInfoHelper, setModalInfoHelper] = useState<boolean>(false);
  const [_countryCode, setCountryCode] = useState<string>();
  const [stateOfBirth, setStateOfBirth] = useState<string>();
  const countryDomTomId = [
    "10",
    "110",
    "151",
    "100",
    "197",
    "155",
    "191",
    "195",
    "244",
    "102",
    "167",
    "103"
  ];
  const [suggestions, setSuggestions] = useState<CityInfoType[] | string[]>();
  const [selected, setSelected] = useState<boolean>(false);
  const [selectedCitizenship, setSelectedCitizenship] = useState<boolean>(false);

  const [locked, setLocked] = useState<boolean>(false);
  const [duplicated, setDuplicated] = useState<boolean | null>(false);

  const { getPostalCodeByCity } = useCityPostalCode();
  const { mutateAsync: getCityPostalCode } = getPostalCodeByCity();
  const [countryOfCitizenship2Toggle, setCountryOfCitizenship2Toggle] = useState(false);
  const [countryOfCitizenship3Toggle, setCountryOfCitizenship3Toggle] = useState(false);
  const [countryOfCitizenship4Toggle, setCountryOfCitizenship4Toggle] = useState(false);
  const [nationality, setNationality] = useState<(string | null)[]>([null, null, null, null]);

  const debouncedSearch = useRef<DebouncedFunc<
    (data: { address: string }) => Promise<void>
  > | null>(null);
  const isActivityRequired =
    userForm?.CSPCode === undefined
      ? true
      : !Object.values(cspWithoutActivity).includes(userForm?.CSPCode);

  useEffect(() => {
    if (user) {
      setUserForm({ ...convert_naturalPerson_DTO(user.naturalPerson), isDirty: false });
      if (countriesData) {
        const codeCountry = countriesData.countries.filter(
          (country: FullCountry) =>
            country.id === +(user.naturalPerson?.Address[0]?.Country as string)
        );
        const idCountry = countriesData.countries.filter(
          (country: FullCountry) => country.alpha2 === user.naturalPerson.nif_country
        );
        if (idCountry && idCountry.length > 0) {
          setCountryCode(idCountry[0].alpha2);
        } else {
          if (codeCountry && codeCountry.length > 0) setCountryCode(codeCountry[0].alpha2);
        }
      }

      if (countryDomTomId.includes(user?.naturalPerson?.countryOfBirth?.toString())) {
        setLocked(true);
        setStateOfBirth(user.naturalPerson.StateOfBirth);
      }
      const arr: (string | null)[] = [null, null, null, null];
      if (user.naturalPerson?.CountryOfCitizenship) {
        arr[0] = user.naturalPerson?.CountryOfCitizenship.toString();
      }
      if (user.naturalPerson?.CountryOfCitizenship2) {
        arr[1] = user.naturalPerson?.CountryOfCitizenship2?.toString();
      }
      if (user.naturalPerson?.CountryOfCitizenship3) {
        arr[2] = user.naturalPerson?.CountryOfCitizenship3?.toString();
      }
      if (user.naturalPerson?.CountryOfCitizenship4) {
        arr[3] = user.naturalPerson?.CountryOfCitizenship4?.toString();
      }
      setNationality(arr);
    }
  }, [user]);

  const onInputChange = (person: NaturalPersonDTO) => setUserForm({ ...person, isDirty: true });

  const onInputTAChange = (person: NaturalPersonDTO) =>
    setUserForm((prevState) => ({
      ...(prevState as NaturalPersonDTO & { isDirty: boolean; isDirtyTA?: boolean }),
      TaxAddress: person.TaxAddress,
      isDirty: prevState?.isDirty as boolean,
      isDirtyTA: true
    }));

  const onSave = async () => {
    if (locked) setLocked(false);

    if (duplicated) {
      // Should not be possible //
      toast.error(t("flow.id_control.input.nationality_add_error"));
      return;
    }

    if (userForm && !isLoadingUser && !isEditing) {
      if (!isIdentityValid(userForm)) {
        window.dispatchEvent(new Event("checkRequiredFields"));
        toast.error(t("input.form_incomplete"));
        return;
      }
      if (!isBirthDateValid(userForm.DateOfBirth?.date)) {
        window.dispatchEvent(new Event("setRequired-birth_date"));
        toast.error(t("flow.id_control.error.must_be_18_plus_error"));
        return;
      }

      if (taxAddresses.isDirty && countriesData && user?.naturalPerson) {
        const [status, messageKey] = await handleTaxAddressesCreate(
          Array.from(taxAddresses.taxAddresses),
          setTaxAddresses,
          countriesData,
          user.naturalPerson,
          createTaxAddress,
          updateTaxAddress,
          validateNif
        );

        if (!status) {
          toast.error(t(messageKey));
          return;
        }

        toast.success(t(messageKey));
      }
      if (userForm.isDirty) await editProfile(userForm);

      const { data: newUser } = await refetchUser();
      if (newUser) {
        setUserForm({ ...convert_naturalPerson_DTO(newUser.naturalPerson), isDirty: false });
      }
      toast.success(t("olkypass.accountPage.notifications.success.profile.update"));
      if (newUser?.naturalPerson.UUID) {
        const cachedFreshUser = {
          email: newUser?.naturalPerson?.AliasEmail?.[0]?.Mail || "",
          name: getFullName(newUser?.naturalPerson),
          avatar: ""
        };
        sessionStorage.setItem("user", JSON.stringify(cachedFreshUser));
      }
    }
  };

  const onClickCity = (value: CityInfoType) => {
    userForm!.StateOfBirth = `${value.nom} (${value.departement.code})`;
    setStateOfBirth(`${value.nom} (${value.departement.code})`);
    onInputChange(userForm as NaturalPersonDTO);
    setSelected(true);
    setLocked(true);
  };

  const handleInputChange = async (address: AddressCreate) => {
    if (locked) return;
    setSelected(false);
    const value = address.city;
    address.zip = "";
    setStateOfBirth(value);
    if (address.city.length === 0 || !address.city) {
      setSuggestions(undefined);
    }
    debouncedSearch.current?.({ address: address.city });
  };

  useEffect(() => {
    debouncedSearch.current = debounce(async (data: { address: string }) => {
      const response = await getCityPostalCode(data);
      const filtredArray = response?.filter(
        (city) => city.nom.toLocaleLowerCase() === data.address.toLocaleLowerCase()
      );

      filtredArray.length > 0
        ? setSuggestions(filtredArray)
        : response?.length > 0
          ? setSuggestions(response)
          : setSuggestions([t("olkypass.accountPage.basicInfo.helper.no_match_city")]);
    }, 250);
    return () => debouncedSearch.current?.cancel();
  }, []);

  const disabled =
    status !== KYCStatusType.DECLARATIVE ||
    (status === KYCStatusType.DECLARATIVE && nextStatus === KYCStatusType.VERIFIED);

  const onModalInfoDisplay = () => setModalInfoHelper(true);

  const onClear = () => {
    setLocked(false);
    setStateOfBirth(``);
  };

  const replaceITem = (arr: (string | null)[], index: number, value: string) => {
    arr[index] = value;
    return arr;
  };

  const getTitle = (title: string, showSeparator: boolean = true) => {
    return (
      <div className="px-6">
        {showSeparator && <hr className={"border-slate-50 dark:border-slate-700"} />}
        {title && <Base className="my-8 text-slate-300 text-xl">{title}</Base>}
      </div>
    );
  };

  // Validation NIF
  const { getNifValidation } = useNifValidation();
  const { mutateAsync: validateNif, isPending: isValidationPending } = getNifValidation();
  // Create/Update Tax Address
  const { createAddressTaxMutation, updateUserAddressTaxMutation } = useTaxAddress();
  const { mutateAsync: createTaxAddress, isPending: isCreationPending } =
    createAddressTaxMutation();
  const { mutateAsync: updateTaxAddress, isPending: isUpdatePending } =
    updateUserAddressTaxMutation();
  //
  const isRequestPending = isValidationPending || isCreationPending || isUpdatePending;

  const [error, _] = useState<string[]>([]);
  const [taxAddresses, setTaxAddresses] = useState<{
    taxAddresses: TaxAddressSchema[];
    isDirty: boolean;
  }>({
    taxAddresses: (user?.naturalPerson.TaxAddress ?? []).map((taxAddress) => ({
      uuid: taxAddress.UUID,
      countryId: taxAddress.country,
      tinExist: taxAddress.tinExist ?? false,
      tin: taxAddress.tin,
      tinCheck: taxAddress.tinCheck ?? null, //JS object contain undefined (TS type casting ...)
      apiCheck: taxAddress.apiCheck ?? false
    })),
    isDirty: false
  });

  const foundDuplicate = (
    nationalityList: (string | null)[],
    newElement: string,
    index: number
  ): boolean => {
    nationalityList[index] = newElement;
    const result: (string | null)[] = [];
    for (const element of nationalityList) {
      if (element === null) result.push(element);
      else if (!result.includes(element)) {
        result.push(element);
      }
    }
    return result.length < nationalityList.length;
  };

  const getTrueSizeNationality = (nationalityList: (string | null)[]) => {
    let count = 0;
    for (const nationality of nationalityList) {
      if (nationality) count++;
    }
    return count;
  };

  const disableNationalityIncrease = () => {
    const trueSize = getTrueSizeNationality(nationality);

    switch (trueSize) {
      case 1:
        if (countryOfCitizenship2Toggle) return true;
        return false;
      case 2:
        if (countryOfCitizenship3Toggle) return true;
        return false;
      case 3:
        if (countryOfCitizenship4Toggle) return true;
        return false;
      default:
        return true;
    }
  };

  useEffect(() => {
    if (selectedCitizenship) {
      if (getTrueSizeNationality(nationality) === 1) setCountryOfCitizenship2Toggle(true);
      if (getTrueSizeNationality(nationality) === 2) setCountryOfCitizenship3Toggle(true);
      if (getTrueSizeNationality(nationality) === 3) setCountryOfCitizenship4Toggle(true);
      setSelectedCitizenship(false);
    }
  }, [selectedCitizenship]);

  return (
    <form>
      <div className="w-full md:mb-6 md:p-0 2xl:mb-0">
        <div className={formStyle.container}>
          {userForm && (
            <>
              <div className="mb-5 flex justify-between">
                <Base className="hidden text-slate-300 md:block">{}</Base>

                {(userForm.isDirty || taxAddresses.isDirty) && (
                  <Button
                    className="m-5 w-max shrink-0"
                    onClick={onSave}
                    icon={<PencilIcon />}
                    theme={Themes.primary}
                    loading={isEditing}
                    disabled={(duplicated ?? false) || isLoadingUser || isEditing}
                  >
                    {t("olkypass.general.save")}
                  </Button>
                )}
              </div>

              {getTitle(t("olkypass.accountPage.basicInfo.tabs.informations"), false)}

              {/* General Infos */}
              {!user?.naturalPerson.DateOfBirth && (
                <Alert
                  type={AlertType.Danger}
                  title={t("olkypass.mainPage.olkypass.fillIdentityData")}
                />
              )}

              <div className={formStyle.containerLine}>
                <div className={formStyle.containerLineTitle}>
                  <Label>{t("olkypass.forms.declarative.name")}</Label>
                </div>
                <div className={formStyle.containerLineContent}>
                  <InputLabel
                    data-tooltip={t("olkypass.accountPage.basicInfo.input.lastName")}
                    placeholder={t("olkypass.accountPage.basicInfo.input.lastName")}
                    required={true}
                    onChange={(LastName) => onInputChange({ ...userForm, LastName })}
                    value={userForm.LastName}
                    disabled={disabled}
                  />
                </div>
                <div className="h-4 w-4" />
                <div className={formStyle.containerLineContent}>
                  <InputLabel
                    data-tooltip={t("olkypass.accountPage.basicInfo.input.firstName")}
                    placeholder={t("olkypass.accountPage.basicInfo.input.firstName")}
                    required={true}
                    onChange={(FirstName) => onInputChange({ ...userForm, FirstName })}
                    value={userForm.FirstName}
                    disabled={disabled}
                  />
                </div>
              </div>

              <div className={formStyle.containerLine}>
                <div className={formStyle.containerLineTitle}>
                  <Label>{t("olkypass.forms.declarative.otherName")}</Label>
                </div>
                <div className={formStyle.containerLineContent}>
                  <InputLabel
                    data-tooltip={t("olkypass.forms.declarative.otherName")}
                    placeholder={t("olkypass.forms.declarative.otherName")}
                    required={false}
                    onChange={(otherFirsName) =>
                      onInputChange({ ...userForm, OtherFirstNames: otherFirsName })
                    }
                    value={(userForm.OtherFirstNames as string) ?? ""}
                    disabled={disabled}
                  />
                </div>
              </div>

              <div className={formStyle.containerLine}>
                <div className={formStyle.containerLineTitle}>
                  <Label>{t("olkypass.forms.declarative.commonName")}</Label>
                </div>
                <div className={formStyle.containerLineContent}>
                  <InputLabel
                    data-tooltip={t("olkypass.accountPage.basicInfo.input.commonName")}
                    placeholder={t("olkypass.accountPage.basicInfo.input.commonName")}
                    required={false}
                    onChange={(CommonName) => onInputChange({ ...userForm, CommonName })}
                    value={userForm.CommonName ?? ""}
                    disabled={disabled}
                  />
                </div>
                <div className="h-4 w-4" />
                <div className={formStyle.containerLineContent}>
                  <InputLabel
                    data-tooltip={t("olkypass.accountPage.basicInfo.input.commonFirstName")}
                    placeholder={t("olkypass.accountPage.basicInfo.input.commonFirstName")}
                    required={false}
                    onChange={(CommonFirstName) => onInputChange({ ...userForm, CommonFirstName })}
                    value={userForm.CommonFirstName ?? ""}
                    disabled={disabled}
                  />
                </div>
              </div>

              {/* Ca c'est nationality BTW */}
              <div className={formStyle.containerLine}>
                <div className={formStyle.containerLineTitle}>
                  <Label>{t("olkypass.forms.declarative.nationality")}</Label>
                </div>
                <div className={formStyle.containerLineContent}>
                  <Dropdown
                    initialValue={userForm?.CountryOfCitizenship?.toString()}
                    placeholder={t("flow.id_control.input.nationality_placeholder")}
                    onChange={(item) => {
                      if (foundDuplicate(nationality.slice(), item.key, 0)) setDuplicated(true);
                      else setDuplicated(false);

                      setNationality((prev) => {
                        const newArray = prev.slice();

                        newArray[0] = item.key;
                        return newArray;
                      });
                      onInputChange({ ...userForm, CountryOfCitizenship: item.key ?? "" });
                    }}
                    required={true}
                    className="w-full"
                    disabled={disabled}
                    search={true}
                    items={
                      countriesData?.countries?.map((country) => ({
                        key: country.id.toString(),
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                      })) ?? []
                    }
                  />
                </div>
              </div>

              {(countryOfCitizenship2Toggle || userForm.CountryOfCitizenship2) && (
                <div className={formStyle.containerLine}>
                  <div className={formStyle.containerLineTitle}>
                    <Label>{t("olkypass.forms.declarative.nationality2")}</Label>
                  </div>
                  <div className={formStyle.containerLineContent}>
                    <Dropdown
                      initialValue={userForm?.CountryOfCitizenship2?.toString()}
                      placeholder={t("flow.id_control.input.nationality_placeholder")}
                      onChange={(item) => {
                        if (foundDuplicate(nationality.slice(), item.key, 1)) setDuplicated(true);
                        else setDuplicated(false);
                        setNationality((prev) => {
                          const newArray = prev.slice();

                          newArray[1] = item.key;
                          return newArray;
                        });
                        onInputChange({
                          ...userForm,
                          CountryOfCitizenship2: item.key ?? ""
                        });
                      }}
                      required={true}
                      className="w-full"
                      disabled={disabled}
                      search={true}
                      items={
                        countriesData?.countries?.map((country) => ({
                          key: country.id.toString(),
                          value: country.defaultName,
                          icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                        })) ?? []
                      }
                    />
                  </div>
                </div>
              )}

              {(countryOfCitizenship3Toggle || userForm.CountryOfCitizenship3) && (
                <div className={formStyle.containerLine}>
                  <div className={formStyle.containerLineTitle}>
                    <Label>{t("olkypass.forms.declarative.nationality3")}</Label>
                  </div>
                  <div className={formStyle.containerLineContent}>
                    <Dropdown
                      initialValue={userForm?.CountryOfCitizenship3?.toString()}
                      placeholder={t("flow.id_control.input.nationality_placeholder")}
                      onChange={(item) => {
                        if (foundDuplicate(nationality.slice(), item.key, 2)) setDuplicated(true);
                        else setDuplicated(false);

                        setNationality((prev) =>
                          replaceITem(prev.slice(), 2, item.key.toUpperCase())
                        );
                        onInputChange({
                          ...userForm,
                          CountryOfCitizenship3: item.key ?? ""
                        });
                      }}
                      required={true}
                      className="w-full"
                      disabled={disabled}
                      search={true}
                      items={
                        countriesData?.countries?.map((country) => ({
                          key: country.id.toString(),
                          value: country.defaultName,
                          icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                        })) ?? []
                      }
                    />
                  </div>
                </div>
              )}

              {(countryOfCitizenship4Toggle || userForm.CountryOfCitizenship4) && (
                <div className={formStyle.containerLine}>
                  <div className={formStyle.containerLineTitle}>
                    <Label>{t("olkypass.forms.declarative.nationality4")}</Label>
                  </div>
                  <div className={formStyle.containerLineContent}>
                    <Dropdown
                      initialValue={userForm?.CountryOfCitizenship4?.toString()}
                      placeholder={t("flow.id_control.input.nationality_placeholder")}
                      onChange={(item) => {
                        if (foundDuplicate(nationality.slice(), item.key, 3)) setDuplicated(true);
                        else setDuplicated(false);
                        setNationality((prev) =>
                          replaceITem(prev.slice(), 3, item.key.toUpperCase())
                        );
                        onInputChange({
                          ...userForm,
                          CountryOfCitizenship4: item.key ?? ""
                        });
                      }}
                      required={true}
                      className="w-full"
                      disabled={disabled}
                      search={true}
                      items={
                        countriesData?.countries?.map((country) => ({
                          key: country.id.toString(),
                          value: country.defaultName,
                          icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                        })) ?? []
                      }
                    />
                  </div>
                </div>
              )}

              {getTrueSizeNationality(nationality) !== MAX_NATIONALITY_NUMBER &&
                !disabled &&
                !countryOfCitizenship4Toggle && (
                  <div className={formStyle.containerLine}>
                    <div className={formStyle.containerLineTitle}></div>
                    <div className={formStyle.containerLineContent}>
                      <Label>{t("flow.id_control.input.add_nationality_checkbox")}</Label>
                      <div className="mt-5 flex">
                        <Checkbox
                          value={selectedCitizenship === true}
                          onChange={() => setSelectedCitizenship(true)}
                          disabled={disableNationalityIncrease()}
                        >
                          <Base className="text-base">
                            {t("flow.id_control.input.add_nationality_checkbox_yes")}
                          </Base>
                        </Checkbox>
                        <Checkbox className="ml-4" value={selectedCitizenship === false}>
                          <Base className="text-base">
                            {t("flow.id_control.input.add_nationality_checkbox_no")}
                          </Base>
                        </Checkbox>
                      </div>
                    </div>
                  </div>
                )}

              {duplicated && (
                <div className={formStyle.containerLine}>
                  <div className={formStyle.containerLineTitle}></div>
                  <Base className="mt-4 text-base text-error">
                    {t("flow.id_control.input.nationality_declarative_add_error")}
                  </Base>
                </div>
              )}

              {getTitle("")}

              <div className={formStyle.containerLine}>
                <div className={formStyle.containerLineTitle}>
                  <Label>{t("olkypass.forms.declarative.birthDate")}</Label>
                </div>
                <div className={formStyle.containerLineContent}>
                  {/*max={DateTime.now().minus({ years: 18 }).toFormat('yyyy-MM-dd')}*/}
                  <InputLabel
                    name="birth_date"
                    required={true}
                    type="date"
                    value={userForm.DateOfBirth?.date?.substring(0, 10)}
                    onChange={(date: string) =>
                      onInputChange({
                        ...userForm,
                        DateOfBirth: { ...userForm.DateOfBirth, date }
                      })
                    }
                    disabled={disabled}
                    dataCy="declarative-birth-date-input"
                  />
                </div>
              </div>

              <div className={formStyle.containerLine}>
                <div className={formStyle.containerLineTitle}>
                  <Label>{t("olkypass.forms.declarative.birthState")}</Label>
                </div>
                <div className={formStyle.containerLineContent}>
                  <Dropdown
                    initialValue={userForm.countryOfBirth?.toString()}
                    onChange={(item) => {
                      onInputChange({
                        ...userForm,
                        countryOfBirth: item.key ?? "",
                        StateOfBirth: ""
                      });
                    }}
                    required={true}
                    className="w-full"
                    disabled={disabled}
                    search={true}
                    items={
                      (countriesData?.countries ?? []).map((country) => ({
                        key: country.id.toString(),
                        value: country.defaultName,
                        icon: <Flag countryCode={country.alpha2.toLowerCase()} />
                      })) ?? []
                    }
                  />
                </div>
              </div>

              {countryDomTomId.includes(userForm.countryOfBirth) ? (
                <>
                  {
                    <div className={formStyle.containerLine}>
                      <div className={formStyle.containerLineTitle}>
                        <Label>{t("olkypass.forms.declarative.birthCity")}</Label>
                      </div>

                      <div className={formStyle.containerLineContent}>
                        <InputLabel
                          value={stateOfBirth}
                          required={true}
                          autoComplete="address-level2"
                          onChange={(value) => {
                            handleInputChange({
                              city: value,
                              tag: "",
                              address: "",
                              country: null,
                              zip: ""
                            });
                          }}
                          disabled={disabled}
                        />
                        {suggestions && suggestions.length > 0 && !selected && (
                          <ul className="absolute top-12 z-50 mt-1 mt-2 max-h-40 w-full list-outside overflow-auto rounded-sm border-2 border-slate-50 bg-white font-semibold text-black duration-300 placeholder:font-semibold placeholder:text-slate-400 hover:list-inside hover:border-slate-100 focus:border-primary focus:bg-white dark:border-slate-800 dark:bg-slate-900 dark:text-white dark:focus:border-primary dark:focus:bg-slate-900 dark:hover:border-slate-800 dark:hover:bg-slate-900 dark:placeholder:text-slate-600">
                            {suggestions.slice(0, 20).map((city: CityInfoType | string) => {
                              if (typeof city === "string") {
                                return <li className="my-1 px-4 py-4">{city}</li>;
                              } else {
                                // Assume city is of type CityInfoType
                                return (
                                  <li
                                    onClick={() => onClickCity(city)}
                                    key={city.code}
                                    className="my-1 cursor-pointer px-4 py-4 hover:rounded-sm hover:bg-slate-50 dark:bg-slate-900 dark:hover:bg-slate-600"
                                  >
                                    {city.nom} ({city.departement.code})
                                  </li>
                                );
                              }
                            })}
                          </ul>
                        )}

                        {locked && !disabled && (
                          <XMarkIcon
                            onClick={() => {
                              onClear();
                              onInputChange({ ...userForm, StateOfBirth: "" });
                            }}
                            className="hover:cursor:pointer focus:pointer absolute top-1/4 right-4 h-6 w-6 translate-y-1 transform text-white-600 hover:text-white focus:outline-none"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </div>
                  }
                </>
              ) : (
                <>
                  <div className={formStyle.containerLine}>
                    <div className={formStyle.containerLineTitle}>
                      <Label>{t("olkypass.forms.declarative.birthCity")}</Label>
                    </div>
                    <div className={formStyle.containerLineContent}>
                      <InputLabel
                        required={true}
                        value={userForm.StateOfBirth}
                        onChange={(StateOfBirth) => {
                          onInputChange({ ...userForm, StateOfBirth });
                        }}
                        disabled={disabled}
                      />
                    </div>
                  </div>
                </>
              )}

              <div className={twMerge("m-5 flex flex-col items-end", disabled ? "" : "hidden")}>
                <Button
                  className="w-max shrink-0"
                  onClick={onModalInfoDisplay}
                  icon={<PencilIcon />}
                  theme={Themes.primary}
                >
                  {t("olkypass.accountPage.basicInfo.button.update_helper")}
                </Button>
              </div>

              {getTitle(t("olkypass.forms.declarative.activity"))}

              <div className={formStyle.containerLine}>
                <div className={formStyle.containerLineTitle}>
                  <Label>{t("olkypass.forms.declarative.csp")}</Label>
                </div>
                <div className={formStyle.containerLineContent}>
                  <Dropdown
                    initialValue={userForm?.CSPCode?.toString()}
                    onChange={(item) => {
                      const hasActivity = !Object.values(cspWithoutActivity).includes(
                        parseInt(item.key)
                      );
                      onInputChange({
                        ...userForm,
                        ActivityCode: hasActivity ? userForm.ActivityCode : 31,
                        CSPCode: item.key ? parseInt(item.key) : -1
                      });
                    }}
                    required={true}
                    className="w-full"
                    search={true}
                    items={
                      (CSPCodes?.cspCodes ?? []).map((cspCode) => ({
                        key: cspCode.Code.toString(),
                        value: cspCode.defaultName
                      })) ?? []
                    }
                  />
                </div>
              </div>
              {!Object.values(cspWithoutActivity).includes(userForm.CSPCode) && (
                <div className={formStyle.containerLine}>
                  <div className={formStyle.containerLineTitle}>
                    <Label>{t("olkypass.forms.declarative.activity")}</Label>
                  </div>
                  <div className={formStyle.containerLineContent}>
                    <Dropdown
                      required={isActivityRequired}
                      initialValue={userForm?.ActivityCode?.toString()}
                      onChange={(item) =>
                        onInputChange({
                          ...userForm,
                          ActivityCode: item.key ? parseInt(item.key) : -1
                        })
                      }
                      className="w-full"
                      search={true}
                      items={
                        (activities?.activities ?? []).map((activity) => ({
                          key: activity.Code.toString(),
                          value: activity.defaultName
                        })) ?? []
                      }
                    />
                  </div>
                </div>
              )}

              {getTitle(t("olkypass.forms.declarative.nif.section"))}

              <FormTaxAdresseIdentity
                taxAddresses={taxAddresses}
                setTaxAddresses={setTaxAddresses}
                countriesData={countriesData}
                isRequestPending={isRequestPending}
                userForm={userForm}
                disabled={disabled}
                error={error}
                onInputChange={onInputTAChange}
              />

              <div className="m-5 flex justify-end">
                {(isLoadingUser || isEditing) && <Loader />}
                {(userForm.isDirty || taxAddresses.isDirty) && !(isLoadingUser || isEditing) && (
                  <Button
                    className="w-max shrink-0"
                    onClick={onSave}
                    icon={<PencilIcon />}
                    theme={Themes.primary}
                    disabled={duplicated ?? false}
                  >
                    {t("olkypass.general.save")}
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
        <ModalUpdateInfoHelper
          open={modalInfoHelper}
          onClose={() => setModalInfoHelper(false)}
        ></ModalUpdateInfoHelper>
      </div>
    </form>
  );
};
